import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { TableContext } from 'src/app/providers/TableProvider';
import AuthService from 'src/shared/api/auth/AuthService';
import TokenService from 'src/shared/api/token/TokenService';
import {
  buildAbsoluteAppUrl,
  OIDC_FASTBOARD_AUTH_URL,
  OIDC_FASTBOARD_CLIENT_ID,
  OIDC_FASTBOARD_REDIRECT_URI,
  OIDC_FASTBOARD_RESPONSE_TYPE,
  OIDC_FASTBOARD_SCOPE,
} from 'src/shared/config';
import { emptyError, routesEnum } from 'src/shared/const';
import { resetWorkspaceState } from 'src/shared/lib/session/resetWorkspaceState';
import { isEmptyObject, setError } from 'src/shared/lib/utils';
import { UserContext } from 'src/shared/store/user';
import { LoginType } from 'src/shared/types';
import { Button, Input } from 'src/shared/ui';

export const LoginForm = () => {
  const location = useLocation();
  const fastboardCodeRef = useRef<string | null>(null);
  const [isFastboardAuthLoading, setIsFastboardAuthLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginType>();

  const disabled = !isEmptyObject(errors) || isFastboardAuthLoading;
  const { setIsUserLoading, setUser, darkMode } = useContext(UserContext);
  const {
    setProjects,
    setForms,
    setSelectedProject,
    setSelectedForm,
    setTableSettings,
    setIsProjectsLoading,
    setIsFormsLoading,
    setIsTableSettingsLoading,
  } = useContext(ProjectsContext);
  const { setTableData } = useContext(TableContext);

  const fastboardAuthCode = useMemo(
    () => new URLSearchParams(location.search).get('code'),
    [location.search],
  );

  const fastboardAuthorizeUrl = useMemo(() => {
    if (!OIDC_FASTBOARD_AUTH_URL || !OIDC_FASTBOARD_CLIENT_ID) {
      return '';
    }

    const params = new URLSearchParams({
      client_id: OIDC_FASTBOARD_CLIENT_ID,
      response_type: OIDC_FASTBOARD_RESPONSE_TYPE,
      scope: OIDC_FASTBOARD_SCOPE,
    });

    const redirectUri = OIDC_FASTBOARD_REDIRECT_URI || buildAbsoluteAppUrl(routesEnum.LOGIN);
    params.set('redirect_uri', redirectUri);

    return `${OIDC_FASTBOARD_AUTH_URL}?${params.toString()}`;
  }, []);

  const resetSession = async () => {
    try {
      await AuthService.logout();
    } catch (_error) {
      // Ignore logout failures and still clear local auth state before a new login attempt.
    } finally {
      resetWorkspaceState({
        setProjects,
        setForms,
        setSelectedProject,
        setSelectedForm,
        setTableSettings,
        setTableData,
        setIsProjectsLoading,
        setIsFormsLoading,
        setIsTableSettingsLoading,
      });
      TokenService.removeTokens();
      setUser(null);
    }
  };

  useEffect(() => {
    if (!fastboardAuthCode || fastboardCodeRef.current === fastboardAuthCode) {
      return;
    }

    fastboardCodeRef.current = fastboardAuthCode;

    const loginByFastboard = async () => {
      setIsUserLoading(true);
      setIsFastboardAuthLoading(true);

      try {
        await resetSession();

        const { data } = await AuthService.fastboardLogin(fastboardAuthCode);
        const { user, ...tokens } = data;

        TokenService.setTokens(tokens);
        setUser(user);
      } catch (e) {
        TokenService.removeTokens();
        setUser(null);
        setError('Не удалось авторизоваться через Fastboard');
      } finally {
        setIsFastboardAuthLoading(false);
        setIsUserLoading(false);
      }
    };

    void loginByFastboard();
  }, [
    fastboardAuthCode,
    setForms,
    setIsFormsLoading,
    setIsProjectsLoading,
    setIsTableSettingsLoading,
    setIsUserLoading,
    setProjects,
    setSelectedForm,
    setSelectedProject,
    setTableData,
    setTableSettings,
    setUser,
  ]);

  const onSubmit: SubmitHandler<LoginType> = async params => {
    try {
      const { data } = await AuthService.login(params);
      const { user, ...tokens } = data;

      TokenService.setTokens(tokens);
      setUser(user);
    } catch (e) {
      if (e.response?.status === 401) {
        setError('Неверный логин или пароль');
      }
    } finally {
      setIsUserLoading(false);
    }
  };

  const onFastboardLogin = () => {
    if (!fastboardAuthorizeUrl) {
      setError('OIDC-конфиг не задан');
      return;
    }

    const startFastboardLogin = async () => {
      setIsUserLoading(true);
      setIsFastboardAuthLoading(true);

      try {
        await resetSession();
        window.location.assign(fastboardAuthorizeUrl);
      } catch (_error) {
        setError('Не удалось сбросить сессию перед входом');
        setIsFastboardAuthLoading(false);
        setIsUserLoading(false);
      }
    };

    void startFastboardLogin();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex min-h-96 w-[33rem] flex-col items-center justify-between gap-4 py-2"
    >
      <div className="font-gothampro-500 text-2xl dark:text-white">Авторизация</div>

      <div className="w-full">
        <Input
          {...register('email', { required: emptyError })}
          error={errors.email?.message}
          placeholder="Введите логин"
          label="Логин"
          containerClassNames="mb-5"
          variant={darkMode ? 'blue' : 'grey'}
        />
        <Input
          {...register('password', { required: emptyError })}
          error={errors.password?.message}
          placeholder="Введите пароль"
          type="password"
          label="Пароль"
          variant={darkMode ? 'blue' : 'grey'}
        />
      </div>

      <div className="font-gothampro-400 flex dark:text-white">
        Забыли пароль?
        <Link to={routesEnum.RESET} className="ml-2 text-blue-1400 underline">
          Восстановить
        </Link>
      </div>

      <Button
        variant={darkMode ? 'greyLight' : 'violet'}
        disabled={disabled}
        classNames="w-96"
        type="submit"
      >
        {isFastboardAuthLoading ? 'Выполняется вход...' : 'Войти'}
      </Button>

      {Boolean(fastboardAuthorizeUrl) && (
        <Button
          variant={darkMode ? 'greyLight' : 'violetOutline'}
          classNames="w-96"
          type="button"
          onClick={onFastboardLogin}
          disabled={isFastboardAuthLoading}
        >
          Войти с помощью Fastboard
        </Button>
      )}
    </form>
  );
};
